<template>
  <index-loader></index-loader>
  <layouts-header></layouts-header>
  <layouts-sidebar></layouts-sidebar>

  <div class="page-wrapper">
    <div class="content">
      <div class="row">
        <div class="col-md-12">
          <dashboard-header :title="title" />
          <sales-rep-dashboard-sections
            class="mt-3"
            :sections="sections"
            :is-loading="isSectionsLoading"
            :issue-sort-by="issueSortBy"
            :issue-sort-dir="issueSortDir"
            @issue-sort="onIssueSort"
          />
          <div class="card sales-rep-dashboard-card" style="position: relative;">
            <div class="section-loader-overlay" v-if="isLoading">
              <span class="loader"></span>
            </div>
            <div class="card-body p-0">
              <div class="statistic-header p-3 border-bottom mb-0">
                <h4 class="mb-0">21st to the 20th</h4>
              </div>
              <div class="p-3 sales-rep-table-section">
                <sales-rep-performance-table
                  :rows="rows"
                  v-model:custom-date-from="customDateFrom"
                  v-model:custom-date-to="customDateTo"
                  :is-loading="isLoading"
                  @apply-custom="onApplyCustom"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { message } from 'ant-design-vue';
import { toApiDateOnly } from '@/util';
import SalesRepPerformanceTable from './sales-rep-performance-table.vue';
import SalesRepDashboardSections from './sales-rep-dashboard-sections.vue';

export default {
  name: 'SalesRepresentativeDashboard',
  components: { SalesRepPerformanceTable, SalesRepDashboardSections },
  data() {
    return {
      title: 'Dashboard',
    };
  },
  setup() {
    const store = useStore();
    const rows = ref([]);
    const meta = ref(null);
    const sections = ref(null);
    const isLoading = ref(false);
    const isSectionsLoading = ref(false);
    const customDateFrom = ref(null);
    const customDateTo = ref(null);
    const issueSortBy = ref('date_of_sale');
    const issueSortDir = ref('desc');

    const applyResponse = (response) => {
      const data = response?.data;
      rows.value = data?.rows || [];
      meta.value = data?.meta || null;
    };

    const getValidationMessage = (error) => {
      const data = error?.response?.data;
      if (data?.errors) {
        const firstKey = Object.keys(data.errors)[0];
        const msgs = data.errors[firstKey];
        if (Array.isArray(msgs) && msgs.length) return msgs[0];
      }
      return data?.message || error?.message || 'Failed to load dashboard data';
    };

    const fetchPerformance = async ({ date_from, date_to, skipGlobalLoader = false } = {}) => {
      isLoading.value = true;
      try {
        const params = { skipGlobalLoader };
        if (date_from && date_to) {
          params.date_from = date_from;
          params.date_to = date_to;
        }
        const response = await store.dispatch('dashboard/getSalesRepPerformance', params);
        applyResponse(response);
      } catch (error) {
        message.error(getValidationMessage(error));
      } finally {
        isLoading.value = false;
      }
    };

    const fetchSections = async ({ skipGlobalLoader = true } = {}) => {
      isSectionsLoading.value = true;
      try {
        const response = await store.dispatch('dashboard/getSalesRepSections', {
          pageSize: 20,
          sort_by: issueSortBy.value,
          sort_dir: issueSortDir.value,
          skipGlobalLoader,
        });
        sections.value = response?.data ?? null;
      } catch (error) {
        message.error(getValidationMessage(error));
      } finally {
        isSectionsLoading.value = false;
      }
    };

    const onIssueSort = (column) => {
      if (issueSortBy.value === column) {
        issueSortDir.value = issueSortDir.value === 'desc' ? 'asc' : 'desc';
      } else {
        issueSortBy.value = column;
        issueSortDir.value = 'desc';
      }
      fetchSections({ skipGlobalLoader: true });
    };

    const onApplyCustom = async () => {
      const from = toApiDateOnly(customDateFrom.value);
      const to = toApiDateOnly(customDateTo.value);
      if (!from || !to) {
        message.error('Please select both From and To dates');
        return;
      }
      if (from > to) {
        message.error('From date must be on or before To date');
        return;
      }
      await fetchPerformance({ date_from: from, date_to: to, skipGlobalLoader: true });
    };

    onMounted(() => {
      fetchPerformance({ skipGlobalLoader: false });
      fetchSections({ skipGlobalLoader: true });
    });

    return {
      rows,
      meta,
      sections,
      isLoading,
      isSectionsLoading,
      customDateFrom,
      customDateTo,
      issueSortBy,
      issueSortDir,
      onApplyCustom,
      onIssueSort,
    };
  },
};
</script>

<style scoped lang="scss">
.section-loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 0.375rem;

  .loader {
    width: 64px;
    height: 64px;
    position: relative;
    animation: rotate 1.5s ease-in infinite alternate;
  }
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}
</style>
